import { CaretDownIcon } from '@radix-ui/react-icons'
import { WandFillIcon } from '@/components/icons/WandFillIcon'
import { WandOutlineIcon } from '@/components/icons/WandOutlineIcon'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu'
import { ControlButton } from './ControlButton'
import { ResetIcon } from '@/components/icons/ResetIcon'

import { useShallow } from 'zustand/react/shallow'
import { useCADQuery } from '@/services/queries/cads'
import {
  useCADPageStore,
  WAND,
  WandButtonState,
  ExplosionsState,
} from '@/state/cad'

import { COLOR_OPTIONS } from '@/constants'

const getWandIcon = (wandSelected: WandButtonState) => {
  switch (wandSelected) {
    case null:
      return <WandFillIcon className="w-4 h-4" />
    case WAND.TRANSPARENCY:
      return <WandOutlineIcon className="w-4 h-4" />
    default:
      return <WandFillIcon className="w-4 h-4" color={wandSelected} />
  }
}

export const AppearanceButton = () => {
  const setCadPageState = useCADPageStore(useShallow((state) => state.setState))
  const parts = useCADPageStore(useShallow((state) => state.transparentParts))
  const setWandSelected = useCADPageStore((state) => state.setWandSelected)
  const wandSelected = useCADPageStore((state) => state.wandSelected)
  const colorMap = useCADPageStore((state) => state.colorMap)
  const exploding = useCADPageStore(
    (state) => state.explosionsState === 'ENABLED',
  )

  const { isLoading, data: cadData } = useCADQuery()
  const gltf = cadData.gltf

  if (isLoading || !gltf) return null

  return (
    <div>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger>
          <ControlButton
            asChild
            icon={
              <div className="flex flex-row items-center">
                {getWandIcon(wandSelected)}
                <CaretDownIcon />
              </div>
            }
            isActive={wandSelected !== null}
            title="Appearance"
            onClick={(e) => {
              if (exploding) {
                if (gltf) gltf.unhighlightParts(colorMap)
                setCadPageState({
                  selectedParts: [],
                  explosionsState: ExplosionsState.DISABLED,
                  isEditModeActive: false,
                })
              }
              if (wandSelected !== null) {
                e.preventDefault()
                setCadPageState({
                  isEditModeActive: false,
                  wandSelected: null,
                })
              }
            }}
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent className="flex flex-row">
          <DropdownMenu>
            <DropdownMenuTrigger>
              <ControlButton
                asChild
                icon={<WandOutlineIcon className="w-4 h-4" />}
                title="Color"
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent className="flex flex-row">
              {COLOR_OPTIONS.map((color) => {
                const colorValue = `#${color.value.toString(16).padStart(6, '0').toUpperCase()}`
                return (
                  <DropdownMenuItem
                    key={color.value}
                    onClick={() => {
                      setWandSelected(
                        wandSelected === colorValue ? null : colorValue,
                      )
                      setCadPageState({
                        isEditModeActive: wandSelected !== colorValue,
                      })
                    }}
                  >
                    <ControlButton
                      className="p-1 w-12 h-12 rounded-md"
                      icon={
                        <WandFillIcon className="w-4 h-4" color={colorValue} />
                      }
                      title={`${color.name}`}
                    />
                  </DropdownMenuItem>
                )
              })}
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenuItem>
            <ControlButton
              isActive={wandSelected === WAND.TRANSPARENCY}
              icon={<WandOutlineIcon className="w-4 h-4" />}
              title="Transparency"
              onClick={() => {
                setWandSelected(WAND.TRANSPARENCY)
                setCadPageState({
                  isEditModeActive: wandSelected !== WAND.TRANSPARENCY,
                })
              }}
            />
          </DropdownMenuItem>
          <DropdownMenuItem>
            <ControlButton
              icon={<ResetIcon className="w-4 h-4" />}
              title="Reset"
              onClick={() => {
                gltf.resetColors()
                gltf.resetTransparency(parts)
                setCadPageState({
                  colorMap: {},
                  transparentParts: [],
                  isEditModeActive: false,
                })
              }}
            />
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
